import { useEffect, useRef, type ReactNode } from 'react';

import { ChevronDownIcon, ChevronUpIcon, CloseLineIcon, MoveIcon } from '../../layout/icons';
import { useDraggableChrome } from '../../layout/useDraggableChrome';
import type { ResultPanelTab } from './sessions';
import '../../styles/rail-drag.css';
import '../../styles/quiz.css';
import '../../styles/submissions.css';

const VIEWS: Array<{ id: ResultPanelTab; title: string }> = [
  { id: 'stats', title: 'Báo cáo' },
  { id: 'detail', title: 'Chi tiết' },
];

export interface ResultsCardFrameProps {
  /** What the results are of, e.g. "bài kiểm tra"; the accessible name is the view title plus this. */
  kindLabel: string;
  subtitle: string;
  /** The đề actions, on their own row under the title. */
  actions: ReactNode;
  /** Báo cáo or Chi tiết, picked on the đề's rail. */
  view: ResultPanelTab;
  /** Only when the tab has no rail: a small Báo cáo | Chi tiết switch in the card instead. */
  onView?: (view: ResultPanelTab) => void;
  /** Folded to a bar so the student's work underneath shows. */
  collapsed: boolean;
  /** The student whose work is on the board, named on the folded bar. */
  studentName: string | null;
  /** Footer Thu gọn: fold to the bar. */
  onCollapse: () => void;
  onExpand: () => void;
  /** Header X: close the card; the session and its student's work stay. */
  onClose: () => void;
  children: ReactNode;
}

// The floating results card, one view at a time: title row (view, subtitle, close), action row, body, Thu gọn footer; folded, one bar
export function ResultsCardFrame({ kindLabel, subtitle, actions, view, onView, collapsed, studentName, onCollapse, onExpand, onClose, children }: ResultsCardFrameProps) {
  // one saved spot for every results card, exam or homework, folded or not
  const drag = useDraggableChrome({ id: 'exam-results', enabled: true, refit: collapsed });
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const refocus = useRef(false);
  const title = VIEWS.filter((item) => item.id === view)[0].title;
  const className = ['quiz-results quiz-results--floating', collapsed ? 'quiz-results--collapsed' : '', drag.dragging ? 'quiz-results--dragging' : ''].filter(Boolean).join(' ');

  // the pressed button unmounts, so focus moves to its counterpart rather than dropping to the page
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    if (toggleRef.current) toggleRef.current.focus();
  }, [collapsed]);

  function fold(next: () => void) {
    refocus.current = true;
    next();
  }

  const handle = (
    <button type="button" className="quiz-results__handle" aria-label="Kéo để di chuyển bảng kết quả, dùng phím mũi tên để chỉnh vị trí" {...drag.handleProps}>
      <MoveIcon size={18} />
    </button>
  );

  if (collapsed) {
    return (
      <aside ref={drag.ref} className={className} aria-label={`${title} ${kindLabel}`}>
        <div className="quiz-results__header quiz-results__header--bar">
          {handle}
          <div className="quiz-results__heading quiz-results__heading--bar">
            <span className="quiz-results__title">{title}</span>
            {studentName ? <span className="quiz-results__bar-student" title={studentName}>{studentName}</span> : null}
          </div>
          <button ref={toggleRef} type="button" className="quiz-results__close" aria-label="Mở rộng bảng kết quả" aria-expanded={false} onClick={() => fold(onExpand)}>
            <ChevronDownIcon size={18} />
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside ref={drag.ref} className={className} aria-label={`${title} ${kindLabel}`}>
      <header className="quiz-results__header">
        {handle}
        <div className="quiz-results__heading">
          <div className="quiz-results__title">{title}</div>
          <div className="quiz-results__subtitle" title={subtitle}>{subtitle}</div>
        </div>
        <button type="button" className="quiz-results__close" aria-label="Đóng bảng kết quả" onClick={onClose}>
          <CloseLineIcon size={18} />
        </button>
      </header>
      {onView ? (
        <div className="results-switch" role="group" aria-label="Xem kết quả">
          {VIEWS.map((item) => (
            <button key={item.id} type="button" aria-pressed={view === item.id} className={view === item.id ? 'results-switch__item is-active' : 'results-switch__item'} onClick={() => onView(item.id)}>{item.title}</button>
          ))}
        </div>
      ) : null}
      <div className="quiz-results__toolbar">{actions}</div>
      <div className="quiz-results__scroll">{children}</div>
      {/* the only fold, bottom-right, within reach on a tall board */}
      <div className="quiz-results__footer">
        <button ref={toggleRef} type="button" className="hw-panel__refresh quiz-results__fold" aria-label="Thu gọn bảng kết quả" aria-expanded={true} onClick={() => fold(onCollapse)}>
          <ChevronUpIcon size={14} /><span>Thu gọn</span>
        </button>
      </div>
    </aside>
  );
}
